<script lang="ts">
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import Button from "$lib/atoms/Button.svelte";
  import { ButtonColor, IconType } from "$types/atoms";
  import { isThemeLight, type ThemeColor } from "$lib/utils/helpers.js";
  import Icon from "$lib/atoms/Icon.svelte";
  import { page } from "$app/stores";
  import { careersMobileFiltersOpen } from "$stores";
  import { scrollTo } from "svelte-scrollto";
  import type {
    CategoryStoryblok,
    FooterCategoryStoryblok,
    SocialLinkStoryblok,
    AnchorLinkWithCssClassStoryblok,
    NavItemStoryblok,
  } from "$types/components";
  import { onMount } from "svelte";
  import StoryblokComponent from "$lib/StoryblokComponent.svelte";

  export let margin: "none" | undefined = undefined;
  export let theme: ThemeColor = "grey";
  export let backgroundColor: "none" | "green" | "charcoal" | undefined =
    undefined;
  export let categories:
    | CategoryStoryblok[]
    | FooterCategoryStoryblok[]
    | undefined;
  export let socials: SocialLinkStoryblok[] = [];
  export let navigation: NavItemStoryblok[] = [];
  export let footerBottomLinks: AnchorLinkWithCssClassStoryblok[] = [];

  const footerTheme = backgroundColor || theme;

  function logoClick() {
    if ($page.params.slug === "") {
      scrollTo({ element: "scroll-to-element-selector", duration: 2000 });
    }
  }

  function asIconType(name: string): IconType {
    return name as IconType;
  }

  let footerElement: HTMLElement;

  function addScripts(urls: string[]) {
    urls.forEach((url) => {
      const script = document.querySelector(`script[src="${url}"]`);
      if (!script) {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = url;
        document.body.appendChild(script);
      }
    });
  }

  onMount(() => {
    addScripts([]);
  });
</script>

<footer
  bind:this={footerElement}
  class={`
    section
    max-w-full
    footer
    ${$careersMobileFiltersOpen ? "!z-[4]" : ""}
    ${footerTheme}
    pt-0
  `}
  class:mt-[80px]={margin !== "none"}
  class:md:mt-[120px]={margin !== "none"}
>
  <div
    class="section-inner max-w-full"
    class:no-categories={!categories || categories.length === 0}
  >
    <div class="section-max-width">
      {#if categories && categories.length > 0}
        <div class="secondary-navigation pb-[64px] md:pb-[90px] lg:pb-[120px]">
          <GridContainer>
            {#each categories as category}
              <GridItem col={12} colMd={6}>
                <div
                  class="footer-nav pt-[16px] md:pt-[24px] pb-[56px] sm:pb-0"
                >
                  <p class="type:section-title">{category.eyebrow}</p>
                  <div class="content">
                    <h3 class="type:h3">{category.headline}</h3>
                    <p class="type:body-medium">{category.text}</p>
                    {#if category.link[0]}
                      <div class="link mt-[24px] md:mt-[40px]">
                        {#if category.link[0].component === "buttonLink"}
                          {#if category.link[0].text && !category.link[0].hideText}
                            <Button
                              href={category.link[0].url}
                              target={category.link[0].target}
                              class="link"
                              color={isThemeLight(footerTheme)
                                ? ButtonColor.WHITE
                                : ButtonColor.BLACK}
                              >{category.link[0].text}</Button
                            >
                          {:else}
                            <Button
                              href={category.link[0].url}
                              target={category.link[0].target}
                              icon={IconType.ARROW_RIGHT}
                              color={isThemeLight(footerTheme)
                                ? ButtonColor.WHITE_LIGHT
                                : ButtonColor.DARK_LIGHT}
                              class="link"
                              label={category.link[0].text}
                            />
                          {/if}
                        {:else}
                          <StoryblokComponent blok={category.link[0]} />
                        {/if}
                      </div>
                    {/if}
                  </div>
                </div>
              </GridItem>
            {/each}
          </GridContainer>
        </div>
      {/if}
      {#if navigation && navigation.length > 0}
        <GridContainer>
          <GridItem col={12}>
            <div
              class="main-navigation pt-[20px] pb-[56px] px-0 md:pt-[45px] md:pb-[200px]"
            >
              <GridContainer noPadding>
                <GridItem col={12}>
                  <div class="split-line" />
                </GridItem>
                <GridItem col={12} colMd={6}>
                  <a class="logo" href="/" on:click={logoClick}>
                    <img
                      alt="Zipline logo"
                      width="78"
                      height="26"
                      src={isThemeLight(footerTheme) ||
                      footerTheme === "charcoal"
                        ? "/svg/logo-white.svg"
                        : "/svg/logo-dark.svg"}
                      loading="lazy"
                    />
                  </a>
                </GridItem>
                <GridItem col={12} colMd={6}>
                  <div
                    class="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-12 max-w-[580px]"
                  >
                    {#each navigation as pageGroup}
                      <div>
                        <h6 class="type:h6-small text-white mb-[1.15em]">
                          {pageGroup.heading}
                        </h6>
                        <ul class="nav">
                          {#if pageGroup.subnav?.[0]?.items}
                            {#each pageGroup.subnav[0].items as pageItem}
                              <li class="nav-item type:body-small">
                                <a
                                  href={pageItem?.subnav?.length > 0
                                    ? "#"
                                    : "/" + pageItem.link?.cached_url}
                                  class="type:body-small {$page.url.pathname ===
                                  '/' + (pageItem.link?.cached_url || '#')
                                    ? 'is-active'
                                    : ''}"
                                >
                                  {pageItem.heading}
                                </a>
                              </li>
                            {/each}
                          {/if}
                        </ul>
                      </div>
                    {/each}
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      {/if}
      <GridContainer>
        <GridItem col={12}>
          <div class="bottom-bar sm:flex sm:justify-between sm:items-center">
            <ul class="terms-policy">
              {#each footerBottomLinks as link}
                <!-- Standard anchor link-->
                {#if link.link[0].component === "anchorLink"}
                  <li class="type:body-small" class:hide={link.hidden}>
                    <a
                      class={link.classes}
                      href={`${
                        link.link[0].link?.cached_url === "#" ? "" : "/"
                      }${link.link[0].link?.cached_url}`}
                    >
                      {link.link[0].text}
                    </a>
                  </li>
                {/if}

                <!-- Custom anchor link -->
                {#if link.link[0].component === "anchorLinkCustom"}
                  <li class="type:body-small" class:hide={link.hidden}>
                    <a
                      id={link.link[0].id}
                      class={link.link[0].classes}
                      href={link.link[0].href}
                      onclick={link.link[0].onclick}
                    >
                      {link.link[0].text}
                    </a>
                  </li>
                {/if}
              {/each}
            </ul>
            <ul class="socials">
              {#each socials as social}
                <li>
                  <a
                    href={social.link?.cached_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.name}
                  >
                    {#if social.name}
                      <Icon type={asIconType(social.name.toLowerCase())} />
                    {/if}
                  </a>
                </li>
              {/each}
            </ul>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  </div>
</footer>

<style lang="scss">
  .hide {
    display: none;
  }

  a {
    text-decoration: none;
    color: var(--color-text-dark);
  }

  .footer {
    &.gray {
      .section-inner {
        background: rgb(var(--color-warm-grey));
      }
    }

    &.green {
      .section-inner {
        background: rgb(var(--color-green-primary));
      }
    }

    &.blue {
      .section-inner {
        background: rgb(var(--color-blue-primary));
      }
    }

    &.green,
    &.blue {
      .section-inner {
        h6,
        h3,
        p {
          color: #ffffff;
        }
      }

      .footer-nav {
        border-top: 1px solid var(--color-stroke-light-rgba);
      }

      .main-navigation {
        border-top: 1px solid var(--color-stroke-light-rgba);
      }

      .bottom-bar {
        border-top: 1px solid var(--color-stroke-light-rgba);
      }

      .terms-policy li {
        opacity: 1;
      }

      .terms-policy,
      .nav,
      .socials {
        a {
          color: var(--color-light-grey-rgba);

          &:hover {
            color: rgb(var(--color-white));
          }

          &:after {
            background: rgb(var(--color-white));
          }
        }
      }
    }

    &.charcoal {
      margin-top: 0;
      padding-left: 0;
      padding-right: 0;

      .section-inner {
        background: #242021;
        border-radius: 0;
      }

      .section-inner {
        h6,
        h3,
        p {
          color: #ffffff;
        }
      }

      .footer-nav {
        border-top: 1px solid var(--color-stroke-light-rgba);
      }

      .bottom-bar {
        border-top: 1px solid var(--color-stroke-light-rgba);
      }

      .terms-policy li {
        opacity: 1;
      }

      .terms-policy,
      .nav,
      .socials {
        a {
          color: var(--color-light-grey-rgba);

          &:hover {
            color: rgb(var(--color-white));
          }

          &:after {
            background: rgb(var(--color-white));
          }
        }
      }
    }

    &.yellow {
      .section-inner {
        background: rgb(var(--color-yellow-primary));
        color: rgb(var(--color-text-dark-grey));
      }
    }

    h6 {
      color: rgb(var(--color-text-dark-grey));
    }

    a {
      color: rgb(var(--color-text-dark-grey));
      position: relative;
      transition: color 0.2s;

      &:hover,
      &.is-active {
        color: rgb(var(--color-text-dark));
      }
    }

    .terms-policy,
    .nav {
      a {
        position: relative;

        &:after {
          content: "";
          position: absolute;
          bottom: em(-5px, 14px);
          left: 0;
          height: 1px;
          width: 100%;
          background: rgb(var(--color-text-dark));
          opacity: 0;
          transition: opacity 0.2s;
        }

        &:hover,
        &.is-active {
          &:after {
            opacity: 1;
          }
        }
      }
    }
  }

  .section-inner {
    border-radius: var(--border-radius-section);

    &:not(.no-categories) {
      background: rgb(var(--color-warm-grey));
      padding: 80px 0 0;
    }
  }

  .footer-nav {
    border-top: 1px solid var(--color-stroke-dark-rgba);

    .type\:section-title {
      margin-bottom: 40px;
    }

    .content {
      padding-right: calc(var(--grid-column-size) + var(--grid-gutter));
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      p {
        margin-top: em(24px, 16px);
        opacity: 0.8;
      }
    }
  }

  .main-navigation {
    border-top: 1px solid var(--color-stroke-dark-rgba);

    .nav-item {
      margin-bottom: em(16px, 14px);
    }

    .nav-link {
      text-decoration: none;
    }
  }

  .logo {
    width: 78px;

    display: block;
    margin-bottom: 40px;

    @include breakpoint("small") {
      margin-bottom: 0px;
    }

    img {
      width: 100%;
    }
  }

  .bottom-bar {
    border-top: 1px solid var(--color-stroke-dark-rgba);
    padding-top: 24px;
    padding-bottom: 40px;
  }

  .socials {
    display: flex;
    align-items: center;
    border-top: 1px solid var(--color-stroke-dark-rgba);
    margin-top: 56px;
    padding-top: 24px;

    @include breakpoint("small") {
      border-top: 0;
      margin-top: 0;
      padding-top: 0;
    }

    li {
      margin-right: 24px;

      @include breakpoint("small") {
        margin-left: 24px;
        margin-right: 0;
      }
    }

    img {
      width: 16px;
    }
  }

  .terms-policy {
    @include breakpoint("small") {
      display: flex;
      align-items: center;
    }

    li {
      margin-bottom: 16px;

      @include breakpoint("small") {
        margin-bottom: 0;
        margin-right: 24px;
      }
    }
  }
</style>
